import React, { useState } from "react";
import {
    User,
    MapPin,
    BookOpen,
    MessageSquare,
    Ticket,
    Activity,
    TrendingUp,
    ShieldCheck,
} from "lucide-react";

import { formatDateLong2, formatDateTime, getAccountStatusColor, getAccountStatusFromCustomer, getStatusCustomerColors, getStatusDotColor } from "../../../../utils/function";
import { ContactItem } from "../types";
import { RaiseTicketModal } from "../../../../components/crm/customer/ticket-pop-up";
import useCrmApi from "../../../../components/hook/crm-req";
import { useBvnStatusVisibility } from "../../../../components/hook/use-bvn-status-visibility";

// ── Shared primitives ────────────────────────────────────────────────────────

const SectionCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
    action?: React.ReactNode;
    className?: string;
}> = ({ icon, title, children, action, className }) => (
    <div className={`bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 ${className || ""}`}>
        <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-medium text-sm">
                {icon}
                <span>{title}</span>
            </div>
            {action}
        </div>
        {children}
    </div>
);

const InfoRow: React.FC<{ label: string; value: string | number | boolean | null | undefined }> = ({
    label,
    value,
}) => (
    <div className="mb-3 last:mb-0">
        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-0.5">{label}</p>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {value !== null && value !== undefined ? String(value) : 'N/A'}
        </p>
    </div>
);

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
    const bgColor = getStatusCustomerColors(status);
    const dotColor = getStatusDotColor(status);
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${bgColor}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
            {status || "Unknown"}
        </span>
    );
};

// ── Mini stat card for conversation health ───────────────────────────────────

const StatCard: React.FC<{
    label: string;
    value: string | number;
    color?: string;
}> = ({ label, value, color = "text-gray-800 dark:text-gray-200" }) => (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1">{label}</p>
        <p className={`text-sm font-semibold ${color}`}>{value}</p>
    </div>
);

// ── Activity timeline item ───────────────────────────────────────────────────

const TimelineItem: React.FC<{ label: string; last?: boolean }> = ({
    label,
    last,
}) => (
    <div className="flex items-start gap-3">
        <div className="flex flex-col items-center shrink-0">
            <span className="w-2 h-2 rounded-full bg-indigo-400 mt-1" />
            {!last && <span className="w-px flex-1 bg-gray-100 dark:bg-gray-800 mt-1 min-h-[16px]" />}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 pb-3">{label}</p>
    </div>
);

// ── Main ─────────────────────────────────────────────────────────────────────

interface BasicInfoTabProps {
    customer: ContactItem;
}

const MOCK_TIMELINE = [
    "Sent First WhatsApp Message",
    "Product Enquiry",
    "Confirmed First Order",
    "Shared first feedback",
    "Sent Second Message",
];

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({ customer }) => {
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
    const showBvnStatus = useBvnStatusVisibility();

    const fullName =
        `${customer?.first_name || customer?.firstName || ""} ${customer?.last_name || customer?.lastName || ""
            }`.trim() || "N/A";

    const rawStatus =
        customer?.custom?.status || customer?.status || "pending";

    const accountStatus = getAccountStatusFromCustomer(customer) as
        | "Active"
        | "Inactive";

    // An "onboarded" tag means this customer is verified, full stop —
    // same rule as the customers table (see mapContactToCustomer/
    // getAccountStatusFromCustomer) — regardless of what custom.status says.
    const isOnboarded = !!customer?.tags?.includes("onboarded");

    const emailVerified =
        isOnboarded || rawStatus === "email_verified" || rawStatus === "verified"
            ? "Verified"
            : "Unverified";

    const phoneVerified =
        isOnboarded ||
            rawStatus === "phone_verified" ||
            rawStatus === "email_verified" ||
            rawStatus === "verified"
            ? "Verified"
            : "Unverified";

    // BVN verification isn't its own status field — it's just whether a
    // bvn_hash has been recorded for this customer at all. A present,
    // non-empty hash means BVN has been verified; null/undefined/"" means
    // it hasn't.
    const bvnStatus = customer?.custom?.bvn_hash ? "Verified" : "Unverified";

    const prefillData = {
        name: fullName,
        phone: customer?.phone || "",
        email: customer?.email || "",
        location: String(customer?.custom?.city || customer?.custom?.location || ""),
    };

    return (
        <div className="p-4 space-y-4">
            {/* Row 1: Basic Info | Activity Timeline | Conversation Health — a
                fluid wrap (not a viewport-width grid) so this stacks to one
                column in a narrow drawer and only sits three-across once the
                drawer is actually wide enough (e.g. expanded), instead of
                cramming three columns into whatever width the drawer has. */}
            <div className="flex flex-wrap gap-4">
                {/* Basic Info */}
                <SectionCard
                    className="min-w-[240px] flex-1"
                    icon={<User size={14} className="text-indigo-500" />}
                    title="Basic Info"
                >
                    <div className="space-y-3">
                        <InfoRow label="Phone Number" value={customer?.phone} />
                        <InfoRow label="Email" value={customer?.email} />
                        <InfoRow
                            label="Location"
                            value={
                                [
                                    customer?.custom?.city,
                                    customer?.custom?.state,
                                ]
                                    .filter(Boolean)
                                    .join(", ") || customer?.custom?.country
                            }
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1">Lead Stage</p>
                                <span className="inline-flex px-2 py-0.5 rounded bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-400 text-xs font-medium">
                                    {customer?.custom?.lead_stage || "New Contact"}
                                </span>
                            </div>
                            <div>
                                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1">Source</p>
                                <span className="inline-flex px-2 py-0.5 rounded bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-400 text-xs font-medium">
                                    {customer?.custom?.source || "N/A"}
                                </span>
                            </div>
                        </div>
                        <InfoRow
                            label="LinkedIn ID"
                            value={customer?.custom?.linkedin_id}
                        />
                        <InfoRow
                            label="First Message"
                            value={
                                customer?.created_at
                                    ? formatDateTime(customer.created_at)
                                    : undefined
                            }
                        />
                    </div>
                </SectionCard>

                {/* Activity Timeline */}
                <SectionCard
                    className="min-w-[240px] flex-1"
                    icon={<Activity size={14} className="text-indigo-500" />}
                    title="Activity Timeline"
                >
                    <div>
                        {MOCK_TIMELINE.map((item, i) => (
                            <TimelineItem
                                key={i}
                                label={item}
                                last={i === MOCK_TIMELINE.length - 1}
                            />
                        ))}
                    </div>
                </SectionCard>

                {/* Conversation Health */}
                <SectionCard
                    className="min-w-[240px] flex-1"
                    icon={<TrendingUp size={14} className="text-indigo-500" />}
                    title="Conversation Health"
                >
                    <div className="space-y-3">
                        <div>
                            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1">Engagement Score</p>
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">High</p>
                            <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                                <div
                                    className="h-full rounded-full bg-green-500"
                                    style={{ width: "80%" }}
                                />
                            </div>
                        </div>
                        <StatCard label="Conversion Probability" value="95%" />
                        <div>
                            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1">Risk Level</p>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-green-300 dark:border-green-500/40 bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                Low
                            </span>
                        </div>
                    </div>
                </SectionCard>
            </div>

            {/* Row 2: Account Details | Location | Account Status */}
            <div className="flex flex-wrap gap-4">
                {/* Account Details */}
                <SectionCard
                    className="min-w-[240px] flex-1"
                    icon={<BookOpen size={14} className="text-indigo-500" />}
                    title="Account Details"
                >
                    <div className="space-y-3">
                        <InfoRow
                            label="Profile Created"
                            value={
                                customer?.created_at
                                    ? formatDateLong2(customer.created_at)
                                    : undefined
                            }
                        />
                        <InfoRow
                            label="Created by"
                            value={customer?.custom?.created_by || '"Agent Name"'}
                        />
                        <InfoRow
                            label="Last Active"
                            value={
                                customer?.updatedAt || customer?.updated_at
                                    ? formatDateTime(
                                        customer?.updatedAt || customer?.updated_at || ""
                                    )
                                    : undefined
                            }
                        />
                        <InfoRow
                            label="Last Interacted with"
                            value={customer?.custom?.last_interacted_with || '"Agent name"'}
                        />
                    </div>
                </SectionCard>

                {/* Location */}
                <SectionCard
                    className="min-w-[240px] flex-1"
                    icon={<MapPin size={14} className="text-indigo-500" />}
                    title="Location"
                >
                    <div className="grid grid-cols-2 gap-3">
                        <InfoRow
                            label="Country"
                            value={customer?.custom?.country as string}
                        />
                        <InfoRow
                            label="State"
                            value={
                                customer?.custom?.user_type === "RECOMMENDER"
                                    ? customer?.custom?.city
                                    : customer?.custom?.state
                            }
                        />
                        <InfoRow
                            label="City"
                            value={
                                customer?.custom?.user_type === "RECOMMENDER"
                                    ? undefined
                                    : customer?.custom?.city
                            }
                        />
                        <InfoRow
                            label="Postal Code"
                            value={customer?.custom?.postal_code as string}
                        />
                    </div>
                </SectionCard>

                {/* Account Status */}
                <SectionCard
                    className="min-w-[240px] flex-1"
                    icon={<ShieldCheck size={14} className="text-indigo-500" />}
                    title="Account Status"
                >
                    <div className="space-y-3">
                        {/* Always two per row — a 3rd badge (BVN, vouchlinks-only)
                            wraps onto its own row rather than cramming all three
                            into one and overlapping in a narrow drawer. */}
                        <div className="grid grid-cols-2 gap-3">
                            {showBvnStatus && (
                                <div>
                                    <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1.5">BVN Status</p>
                                    <StatusBadge status={bvnStatus} />
                                </div>
                            )}
                            <div>
                                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1.5">WhatsApp</p>
                                <StatusBadge status={phoneVerified} />
                            </div>
                            <div>
                                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1.5">Email</p>
                                <StatusBadge
                                    status={emailVerified === "Verified" ? "Verified" : "Pending"}
                                />
                            </div>
                        </div>
                        <div>
                            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1.5">Account Status</p>
                            <span
                                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium capitalize ${getAccountStatusColor(
                                    accountStatus
                                )}`}
                            >
                                {accountStatus}
                            </span>
                        </div>
                    </div>
                </SectionCard>
            </div>

            {/* Row 3: Recent Conversations preview | Recent Tickets preview */}
            <div className="flex flex-wrap gap-4">
                {/* Recent Conversations */}
                <SectionCard
                    className="min-w-[280px] flex-1"
                    icon={<MessageSquare size={14} className="text-indigo-500" />}
                    title="Recent Conversations"
                    action={
                        <button className="text-xs text-indigo-600 hover:underline font-medium">
                            View All Conversations
                        </button>
                    }
                >
                    <PreviewConversationList contactId={customer.id} />
                </SectionCard>

                {/* Recent Tickets */}
                <SectionCard
                    className="min-w-[280px] flex-1"
                    icon={<Ticket size={14} className="text-indigo-500" />}
                    title="Recent Tickets"
                    action={
                        <button
                            onClick={() => setIsTicketModalOpen(true)}
                            className="text-xs text-indigo-600 hover:underline font-medium"
                        >
                            + New Ticket
                        </button>
                    }
                >
                    <PreviewTicketList contactId={customer.id} />
                </SectionCard>
            </div>

            <RaiseTicketModal
                isOpen={isTicketModalOpen}
                onClose={() => setIsTicketModalOpen(false)}
                conversationId={null}
                prefillCustomerData={prefillData}
            />
        </div>
    );
};

// ── Inline preview lists (fetch 2 items) ─────────────────────────────────────

const PreviewConversationList: React.FC<{ contactId: string }> = ({
    contactId,
}) => {
    const token = localStorage.getItem("authToken");
    const { makeRequest, loading } = useCrmApi(
        `/conversation/${contactId}`,
        "GET",
        { Authorization: `Bearer ${token}` }
    );
    const [items, setItems] = React.useState<any[]>([]);

    React.useEffect(() => {
        makeRequest(undefined, { per_page: 2 }).then(([res]) => {
            setItems(res?.results?.slice(0, 2) || []);
        });
    }, [contactId]);

    if (loading)
        return <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-2">Loading…</p>;

    if (!items.length)
        return (
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-2">No conversations yet.</p>
        );

    return (
        <div className="space-y-3">
            {items.map((c: any) => (
                <div key={c.id} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-500/15 flex items-center justify-center shrink-0 mt-0.5">
                        <MessageSquare size={12} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                            {c.last_message?.body || c.title}
                        </p>
                        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">
                            {c.last_message_at
                                ? formatDateTime(c.last_message_at)
                                : ""}
                        </p>
                        <button className="text-xs text-indigo-500 hover:underline mt-0.5">
                            View Conversation
                        </button>
                    </div>
                </div>
            ))}
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
                Showing last {items.length} conversation
                {items.length !== 1 ? "s" : ""}
            </p>
        </div>
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars

const TICKET_STATUS_COLORS: Record<string, string> = {
    open: "bg-yellow-50 dark:bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/40",
    "to do": "bg-yellow-50 dark:bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/40",
    "in progress": "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/40",
    closed: "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700",
    resolved: "bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/40",
};

const PreviewTicketList: React.FC<{ contactId: string }> = ({
    contactId,
}) => {
    const token = localStorage.getItem("authToken");
    const { makeRequest, loading } = useCrmApi(`/tickets/${contactId}`, "GET", {
        Authorization: `Bearer ${token}`,
    });
    const [items, setItems] = React.useState<any[]>([]);
    const [total, setTotal] = React.useState(0);

    React.useEffect(() => {
        makeRequest(undefined, { per_page: 3 }).then(([res]) => {
            setItems(res?.results?.slice(0, 3) || []);
            setTotal(res?.total || 0);
        });
    }, [contactId]);

    if (loading)
        return <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-2">Loading…</p>;

    if (!items.length)
        return <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-2">No tickets yet.</p>;

    return (
        <div className="space-y-3">
            {items.map((t: any) => {
                const statusKey = (t.status || "open").toLowerCase().replace("_", " ");
                const statusColor =
                    TICKET_STATUS_COLORS[statusKey] ||
                    "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700";
                return (
                    <div key={t.id} className="flex items-start gap-3">
                        <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 mt-0.5 ${statusColor}`}
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            {t.status
                                ?.replace("_", " ")
                                .replace(/\b\w/g, (c: string) => c.toUpperCase()) ||
                                "Open"}
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                                {t.issue_type || "Support Ticket"}
                            </p>
                            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
                                {t.ticket_id
                                    ? `TicketID #${t.ticket_id}`
                                    : t.id?.slice(0, 8)}{" "}
                                ·{" "}
                                {t.created_at ? `Created: ${formatDateLong2(t.created_at)}` : ""}
                            </p>
                        </div>
                        <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0 capitalize">
                            {t.priority || "—"}
                        </span>
                    </div>
                );
            })}
            {total > 3 && (
                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
                    Showing 3 of {total} tickets
                </p>
            )}
        </div>
    );
};

export default BasicInfoTab;